import { Table, SortTH } from 'foundations-components/transitional/components/Table';
import { useRef } from 'react';
import { useTheme } from 'foundations-theming/utils';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import UserAvatarLink from '../common/UserAvatarLink';
import { Small } from 'foundations-components/transitional/components/Small';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PlaylistLink from './PlaylistLink';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import { usePlaylistAssignments } from '../../queries/playlistQueries';
import ACTableSkeleton from 'academy-common-lib/components/loading/ACTableSkeleton';
import { NavMarker } from 'react-rhumb';
import ApiError from 'academy-common-lib/components/error/ApiError';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import MyTeamTablePagination from '../common/TablePagination';
import { useSyncTableStateWithUrl, usePlaylistsTableStore } from '../../store/myTeamTableStateStore';
import { useTableSorting } from '../../hooks/tableHooks';
import { getOffsetPosition, getPaginatedPosition } from '../../utils/paginationUtils';
import EmptyTable from '../common/EmptyTable';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { getPlaylistTypeLabel, getProgressPercent, getStatus } from '../../utils/playlistsUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PAGE_SIZE = DEFAULT_PAGE_SIZE;
const PlaylistsTable = () => {
  var _playlistAssignments$;
  const {
    playlistStatus: tab
  } = useParams();
  const status = getStatus(tab);
  const isAllTab = status === undefined;
  const theme = useTheme();
  const {
    isDesktop
  } = useMediaQuery();
  const playlistsTableStore = usePlaylistsTableStore();
  useSyncTableStateWithUrl(playlistsTableStore);
  const {
    state: {
      page,
      sortBy,
      sortOrder,
      search,
      teams,
      users
    },
    actions: {
      setPage,
      setSortBy,
      setSortOrder,
      resetTableState
    }
  } = playlistsTableStore;
  const {
    handleSortChange,
    getSortDirectionForColumn
  } = useTableSorting({
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder
  });
  const offsetPosition = getOffsetPosition(page, PAGE_SIZE);
  const {
    playlistAssignments,
    playlistAssignmentsLoading,
    playlistAssignmentsError
  } = usePlaylistAssignments({
    pageLength: PAGE_SIZE,
    offset: offsetPosition,
    sortOrder,
    sortField: sortBy,
    playlistNameQuery: search,
    teams,
    users,
    status
  });
  const totalResultsRef = useRef(0);

  // Reset table state when switching tabs, but not on mount
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    resetTableState();
  }, [status, resetTableState]);
  if (playlistAssignmentsError) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "PLAYLISTS_TABLE_API_ERROR",
      children: /*#__PURE__*/_jsx(ApiError, {
        error: {
          status: 500
        }
      })
    });
  }
  if (!playlistAssignmentsLoading && playlistAssignments) {
    totalResultsRef.current = playlistAssignments.total;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [playlistAssignmentsLoading ? /*#__PURE__*/_jsx(ACTableSkeleton, {
      columns: isAllTab ? 5 : 4,
      condensed: true,
      rows: PAGE_SIZE
    }) : playlistAssignments !== null && playlistAssignments !== void 0 && (_playlistAssignments$ = playlistAssignments.results) !== null && _playlistAssignments$ !== void 0 && _playlistAssignments$.length ? /*#__PURE__*/_jsxs(Table, {
      condensed: true,
      "data-test-id": "playlists-table",
      hover: true,
      responsive: true,
      children: [/*#__PURE__*/_jsxs("colgroup", {
        children: [/*#__PURE__*/_jsx("col", {
          style: {
            width: '35%',
            borderInlineEnd: `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`
          }
        }), /*#__PURE__*/_jsx("col", {
          style: {
            width: '20%'
          }
        }), status !== 'COMPLETED' && /*#__PURE__*/_jsx("col", {
          style: {
            width: '15%'
          }
        }), /*#__PURE__*/_jsx("col", {
          style: {
            width: '15%'
          }
        }), status !== 'INCOMPLETE' && /*#__PURE__*/_jsx("col", {
          style: {
            width: '15%'
          }
        })]
      }), /*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsxs("tr", {
          children: [/*#__PURE__*/_jsx(SortTH, {
            onClick: () => handleSortChange('PLAYLIST_NAME'),
            sort: getSortDirectionForColumn('PLAYLIST_NAME'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.myTeam.playlists.tableColumns.playlist"
            })
          }), /*#__PURE__*/_jsx(SortTH, {
            onClick: () => handleSortChange('TEAM_MEMBER_NAME'),
            sort: getSortDirectionForColumn('TEAM_MEMBER_NAME'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.myTeam.playlists.tableColumns.name"
            })
          }), status !== 'COMPLETED' && /*#__PURE__*/_jsx(SortTH, {
            onClick: () => handleSortChange('PROGRESS'),
            sort: getSortDirectionForColumn('PROGRESS'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.myTeam.playlists.tableColumns.progress"
            })
          }), /*#__PURE__*/_jsx(SortTH, {
            onClick: () => handleSortChange('DUE_DATE'),
            sort: getSortDirectionForColumn('DUE_DATE'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.myTeam.playlists.tableColumns.dueDate"
            })
          }), status !== 'INCOMPLETE' && /*#__PURE__*/_jsx(SortTH, {
            onClick: () => handleSortChange('COMPLETED_DATE'),
            sort: getSortDirectionForColumn('COMPLETED_DATE'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.myTeam.playlists.tableColumns.completed"
            })
          })]
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: playlistAssignments.results.map((playlistAssignment, index) => {
          const progressPercent = getProgressPercent(playlistAssignment.playlistProgress);
          const position = getPaginatedPosition(offsetPosition, index);
          return /*#__PURE__*/_jsxs("tr", {
            "data-test-id": "playlists-table-data-row",
            children: [/*#__PURE__*/_jsxs("td", {
              "data-test-id": "playlist-cell",
              children: [/*#__PURE__*/_jsx(PlaylistLink, {
                playlistId: playlistAssignment.playlistId,
                playlistName: playlistAssignment.playlistName,
                position: position
              }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(Small, {
                children: getPlaylistTypeLabel(readUnhandledEnumCase(playlistAssignment.playlistType))
              })]
            }), /*#__PURE__*/_jsx("td", {
              "data-test-id": "user-cell",
              children: /*#__PURE__*/_jsx(UserAvatarLink, {
                displayAvatar: isDesktop,
                position: position,
                userEmail: playlistAssignment.userEmail,
                userId: playlistAssignment.userId,
                userName: playlistAssignment.userName
              })
            }), status !== 'COMPLETED' && /*#__PURE__*/_jsx("td", {
              "data-test-id": "progress-cell",
              children: /*#__PURE__*/_jsx(FormattedPercentage, {
                value: progressPercent
              })
            }), /*#__PURE__*/_jsx("td", {
              "data-test-id": "due-date-cell",
              children: playlistAssignment.dueDate ? /*#__PURE__*/_jsx(FormattedDateTime, {
                format: "ll",
                type: "utc",
                value: playlistAssignment.dueDate
              }) : '--'
            }), status !== 'INCOMPLETE' && /*#__PURE__*/_jsx("td", {
              "data-test-id": "completed-date-cell",
              children: playlistAssignment.completedDate ? /*#__PURE__*/_jsx(FormattedDateTime, {
                format: "ll",
                type: "utc",
                value: playlistAssignment.completedDate
              }) : '--'
            })]
          }, `${playlistAssignment.playlistId}-${playlistAssignment.userId}`);
        })
      })]
    }) : /*#__PURE__*/_jsx(EmptyTable, {
      navMarkerName: "PLAYLISTS_TABLE_EMPTY"
    }), /*#__PURE__*/_jsx(MyTeamTablePagination, {
      onPageChange: setPage,
      page: page,
      pageSize: PAGE_SIZE,
      totalResults: totalResultsRef.current
    })]
  });
};
export default PlaylistsTable;